/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type ReactElement, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { Spinner } from '@/components/spinner';
import { CollapsibleLoadingContext } from './collapsible-loading-context';

export interface CollapsibleSectionProps {
    /** The label rendered inside the summary row */
    label: string;
    /** Optional content rendered after the label (e.g. AI badge) */
    labelSupplement?: ReactNode;
    /** Content revealed when the section is open */
    children: ReactNode;
    /** Whether the section starts open. Defaults to false. */
    defaultOpen?: boolean;
    /** Additional classes forwarded to the outer <details> element */
    className?: string;
}

/**
 * A native HTML `<details>`/`<summary>` collapsible section.
 *
 * Children are lazy-mounted: they are not rendered until the section is opened
 * for the first time (or immediately if `defaultOpen` is true). Once mounted,
 * children remain in the DOM so their state (e.g. fetched data) is preserved
 * across subsequent open/close cycles.
 *
 * The `open` attribute is React-controlled. Clicking the summary mounts children
 * and sets a pending flag but does NOT expand the section immediately. The section
 * only opens once `isLoading` is false, preventing any layout shift from async
 * children (e.g. ProductAdapterSection). For synchronous children the section
 * opens on the same tick since `isLoading` is never set.
 *
 * While a child signals loading via `CollapsibleLoadingContext`, the chevron
 * icon is replaced with a spinner.
 */
export default function CollapsibleSection({
    label,
    labelSupplement,
    children,
    defaultOpen = false,
    className,
}: CollapsibleSectionProps): ReactElement {
    // Whether the section is visually open (controls the open attribute).
    const [isOpen, setIsOpen] = useState(defaultOpen);
    // Whether children have been mounted at least once (for lazy mounting).
    const [hasOpened, setHasOpened] = useState(defaultOpen);
    // Set to true when the user clicks to open while content is still loading.
    const [pendingOpen, setPendingOpen] = useState(false);
    // Rendered loading state — drives the spinner visibility.
    const [isLoading, setIsLoadingState] = useState(false);
    // Ref mirror of isLoading. Written synchronously inside setLoading so the
    // open-decision effect (below) can read the true current value without
    // waiting for a child's queued state update to commit. This works because
    // React flushes children's effects before parents', so by the time the
    // parent effect reads the ref, the child has already written to it.
    const isLoadingRef = useRef(false);

    const loadingContextValue = useMemo(
        () => ({
            setLoading: (loading: boolean) => {
                isLoadingRef.current = loading;
                setIsLoadingState(loading);
            },
        }),
        [] // setIsLoadingState is a stable state setter — no deps needed
    );

    // Open the section once both conditions hold: the user requested it
    // (pendingOpen) and no child is loading. Reading the ref rather than the
    // isLoading state ensures we see the child's synchronous write even before
    // its queued state update has re-rendered. isLoading is still listed in
    // deps so this effect re-runs when loading state changes.
    useEffect(() => {
        if (pendingOpen && !isLoadingRef.current) {
            setIsOpen(true);
            setPendingOpen(false);
        }
    }, [pendingOpen, isLoading]); // isLoading re-triggers this effect when the child finishes loading

    const handleSummaryClick = (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        if (isOpen) {
            setIsOpen(false);
        } else {
            setHasOpened(true);
            setPendingOpen(true);
        }
    };

    return (
        <CollapsibleLoadingContext value={loadingContextValue}>
            <details className={cn('group border-b border-border', className)} open={isOpen || undefined}>
                <summary
                    className="flex items-center justify-between gap-4 text-base font-medium text-foreground cursor-pointer list-none [&::-webkit-details-marker]:hidden hover:bg-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foreground transition-colors"
                    onClick={handleSummaryClick}>
                    <span className="flex items-center gap-2">
                        {label}
                        {labelSupplement}
                    </span>
                    {isLoading || pendingOpen ? (
                        <Spinner size="sm" />
                    ) : isOpen ? (
                        <svg
                            aria-hidden="true"
                            className="pdp-collapsible-icon text-foreground pointer-events-none size-5 shrink-0"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                        >
                            <line x1="2" y1="12" x2="22" y2="12" />
                        </svg>
                    ) : (
                        <svg
                            aria-hidden="true"
                            className="pdp-collapsible-icon text-foreground pointer-events-none size-5 shrink-0"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                        >
                            <line x1="2" y1="12" x2="22" y2="12" />
                            <line x1="12" y1="2" x2="12" y2="22" />
                        </svg>
                    )}
                </summary>
                <div className="pb-4">{hasOpened && children}</div>
            </details>
        </CollapsibleLoadingContext>
    );
}
